import { Body, Button, H2, H3 } from "@datapay-by-datacom/design-system";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { FileDropzone, WorkflowActionBar } from "../../shared/components";
import { UnsavedProgressConfirmModal } from "../../onboarding/components/UnsavedProgressConfirmModal";
import { useUnsavedProgressNavigationGuard } from "../../onboarding/hooks/useUnsavedProgressNavigationGuard";
import { AssignmentDateColumnsFields } from "../components/AssignmentDateColumnsFields";
import { MappingTablesPanel } from "../components/MappingTablesPanel";
import { Pagination } from "../components/Pagination/Pagination";
import {
  DEFAULT_PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
  PAY_COMPONENT_IMPORT_BASE_PATH,
  clampPageSize,
} from "../config";
import { useCompanyMappings } from "../hooks/useCompanyMappings";
import { useComponentsParameters } from "../hooks/useComponentsParameters";
import { useDetectComponentColumn } from "../hooks/useDetectComponentColumn";
import {
  mappingSelectionKey,
  useMappingElements,
  type MappingLayoutMode,
} from "../hooks/useMappingElements";
import { useMapColumns } from "../hooks/useMapColumns";
import { usePagination } from "../hooks/usePagination";
import { useSaveMappings } from "../hooks/useSaveMappings";
import { useWizard } from "../hooks/useWizard";
import type { ComponentParameter } from "../types";
import { formatUserFacingError } from "../utils/apiErrorMessage";
import {
  deriveComponentInstances,
  type ComponentInstances,
} from "../utils/componentAmbiguity";
import { distinctComponentNames } from "../utils/componentNames";
import {
  ImportRowLimitError,
  formatDuplicateHeadersWarning,
  parseCsvStreaming,
} from "../utils";
import {
  detectEndDateColumn,
  detectStartDateColumn,
} from "../utils/dateColumns";
import { RESERVED_LC, resolveParam } from "../utils/mappedRows";
import { formatMissingMappingsMessage } from "../utils/mappingValidation";
import { csvTextToFile } from "../utils/csvTextToFile";
import { clearWizardSession } from "../utils/wizardSession";
import { CancelImportConfirmModal } from "./CancelImportConfirmModal";
import { PayComponentRowEditModal } from "./PayComponentRowEditModal";
import { addEditedRowIndex } from "./payComponentImportClustering";
import {
  applyPayComponentRowDraftToPreview,
  mappedRowFromPreviewSource,
  serializePreviewToCsvText,
  type PayComponentRowDraft,
} from "./payComponentRowEditorDraft";
import {
  groupPreviewRowsByComponent,
  isUnrecognizedPreviewGroup,
  summarizeUnrecognizedComponents,
  type PreviewComponentGroup,
} from "./previewRowsByComponent";
import { ReplaceCsvConfirmModal } from "./ReplaceCsvConfirmModal";
import styles from "./UploadAndMapScreen.module.css";

type PreviewLayoutMode = "flat" | "byComponent";

function isReservedColumn(header: string): boolean {
  return RESERVED_LC.has(header.toLowerCase());
}

const ALLOWED_NAV_PATHS = [
  PAY_COMPONENT_IMPORT_BASE_PATH,
  `${PAY_COMPONENT_IMPORT_BASE_PATH}/map`,
  `${PAY_COMPONENT_IMPORT_BASE_PATH}/review`,
  `${PAY_COMPONENT_IMPORT_BASE_PATH}/errors`,
  `${PAY_COMPONENT_IMPORT_BASE_PATH}/results`,
];

export function UploadAndMapScreen() {
  const {
    companyCode,
    state,
    updateState,
    invalidateValidation,
    reportUiBusy,
    discardUnsavedProgress,
    runImport,
    isValidating,
    isCommitting,
  } = useWizard();
  const navigate = useNavigate();

  const [isParsing, setIsParsing] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [pendingReplacementFile, setPendingReplacementFile] =
    useState<File | null>(null);
  const [mappingMode, setMappingMode] = useState<MappingLayoutMode>("grouped");
  const [previewLayout, setPreviewLayout] =
    useState<PreviewLayoutMode>("byComponent");
  const [openPreviewGroups, setOpenPreviewGroups] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const previewGroupsSeededRef = useRef(false);
  const fileReaderRef = useRef<FileReader | null>(null);
  const parseMountedRef = useRef(true);
  useEffect(() => {
    parseMountedRef.current = true;
    return () => {
      parseMountedRef.current = false;
      fileReaderRef.current?.abort();
    };
  }, []);
  const [editingRowIndex, setEditingRowIndex] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [mappingValidationError, setMappingValidationError] = useState<
    string | null
  >(null);
  const [highlightUnmappedScopes, setHighlightUnmappedScopes] = useState(false);

  const preview = state.preview;
  const headers = useMemo(() => preview?.headers ?? [], [preview]);
  const rows = useMemo(() => preview?.rows ?? [], [preview]);

  const hasPreview = !!preview && preview.rows.length > 0;
  const { isBlocked, stay, leaveAnyway } = useUnsavedProgressNavigationGuard(
    hasPreview && !isCommitting,
    ALLOWED_NAV_PATHS,
  );

  const handleLeaveAnyway = () => {
    discardUnsavedProgress();
    leaveAnyway();
  };

  const processFile = async (file: File) => {
    setParseError(null);
    setIsParsing(true);
    fileReaderRef.current?.abort();
    const reader = new FileReader();
    fileReaderRef.current = reader;
    reader.onload = async (event) => {
      if (!parseMountedRef.current) return;
      const text = event.target?.result;
      if (typeof text !== "string") {
        setIsParsing(false);
        return;
      }
      try {
        const parsed = await parseCsvStreaming(text);
        updateState({
          file,
          csvText: text,
          preview: parsed,
          componentColumn: null,
          startDateColumn: null,
          endDateColumn: null,
          importResult: null,
          validationStale: false,
          editedRowIndexes: [],
        });
      } catch (err) {
        updateState({
          file: null,
          csvText: null,
          preview: null,
          componentColumn: null,
          startDateColumn: null,
          endDateColumn: null,
        });
        setParseError(
          err instanceof ImportRowLimitError
            ? err.message
            : "Could not parse this CSV file. Check the format and try again.",
        );
      } finally {
        setIsParsing(false);
      }
    };
    reader.readAsText(file);
  };

  const handleFileChange = (file: File | File[] | null) => {
    if (!file || Array.isArray(file)) {
      clearWizardSession();
      updateState({
        file: null,
        csvText: null,
        preview: null,
        componentColumn: null,
        startDateColumn: null,
        endDateColumn: null,
        importResult: null,
        validationStale: false,
        editedRowIndexes: [],
      });
      setParseError(null);
      return;
    }
    if (preview && preview.rows.length > 0) {
      setPendingReplacementFile(file);
      return;
    }
    void processFile(file);
  };

  const handleConfirmReplacement = () => {
    const file = pendingReplacementFile;
    setPendingReplacementFile(null);
    clearWizardSession();
    updateState({
      file: null,
      csvText: null,
      preview: null,
      componentColumn: null,
      startDateColumn: null,
      endDateColumn: null,
      importResult: null,
      componentResolutions: {},
      validationStale: false,
      editedRowIndexes: [],
    });
    if (file) {
      void processFile(file);
    }
  };

  const { detectedColumn } = useDetectComponentColumn(
    companyCode,
    preview,
    state.mypayEnvironment,
  );

  const activeComponentColumn =
    state.componentColumn ?? detectedColumn ?? preview?.componentColumn ?? "";
  const activeStartDateColumn = state.startDateColumn ?? "";
  const activeEndDateColumn = state.endDateColumn ?? "";

  useEffect(() => {
    if (state.componentColumn == null && detectedColumn) {
      updateState({ componentColumn: detectedColumn });
      invalidateValidation();
    }
  }, [detectedColumn, state.componentColumn, updateState, invalidateValidation]);

  useEffect(() => {
    if (!preview) return;
    const updates: {
      startDateColumn?: string;
      endDateColumn?: string;
    } = {};
    if (state.startDateColumn == null) {
      const detected = detectStartDateColumn(headers);
      if (detected) updates.startDateColumn = detected;
    }
    if (state.endDateColumn == null) {
      const detected = detectEndDateColumn(headers);
      if (detected) updates.endDateColumn = detected;
    }
    if (Object.keys(updates).length > 0) {
      updateState(updates);
      invalidateValidation();
    }
  }, [
    preview,
    headers,
    state.startDateColumn,
    state.endDateColumn,
    updateState,
    invalidateValidation,
  ]);

  const componentNames = useMemo(
    () => distinctComponentNames(rows, activeComponentColumn),
    [rows, activeComponentColumn],
  );

  const paramsQuery = useComponentsParameters(
    companyCode,
    componentNames,
    state.mypayEnvironment,
  );
  const mappingsQuery = useCompanyMappings(companyCode);

  const dataColumns = useMemo(
    () =>
      headers.filter(
        (h) =>
          !RESERVED_LC.has(h.toLowerCase()) &&
          h !== activeComponentColumn &&
          h !== activeStartDateColumn &&
          h !== activeEndDateColumn,
      ),
    [
      headers,
      activeComponentColumn,
      activeStartDateColumn,
      activeEndDateColumn,
    ],
  );

  const paramsReady =
    !paramsQuery.isLoading && paramsQuery.data !== undefined;
  const { elements, componentNamesByElement, unknownComponents, hasSharedElements } =
    useMappingElements(componentNames, paramsQuery.data ?? {}, mappingMode);
  const unknownComponentSet = useMemo(
    () => new Set(unknownComponents),
    [unknownComponents],
  );
  const unrecognizedSummary = useMemo(
    () =>
      summarizeUnrecognizedComponents({
        rows,
        componentColumn: activeComponentColumn,
        unknownComponentNames: unknownComponents,
      }),
    [rows, activeComponentColumn, unknownComponents],
  );
  const showUnrecognizedWarning =
    paramsReady &&
    (unrecognizedSummary.unknownNames.length > 0 ||
      (Boolean(activeComponentColumn) &&
        unrecognizedSummary.blankRowCount > 0));

  const componentInstances = useMemo(
    () => deriveComponentInstances(paramsQuery.data ?? {}),
    [paramsQuery.data],
  );
  const resolutions = state.componentResolutions ?? {};
  const ambiguousComponents = useMemo(
    () =>
      componentNames
        .map((name) => ({ name, instances: componentInstances[name] }))
        .filter(
          (c): c is { name: string; instances: ComponentInstances } =>
            c.instances?.isAmbiguous === true && c.instances.options.length > 0,
        ),
    [componentNames, componentInstances],
  );
  const hasUnresolvedAmbiguity = ambiguousComponents.some(
    ({ name }) => !resolutions[name],
  );

  const handleSelectInstance = (name: string, companyComponentId: string) => {
    invalidateValidation();
    const next = { ...(state.componentResolutions ?? {}) };
    if (companyComponentId === "") {
      delete next[name];
    } else {
      next[name] = companyComponentId;
    }
    updateState({ componentResolutions: next });
  };

  const initialSelections = useMemo(() => {
    const mappings = mappingsQuery.data ?? [];
    const sel: Record<string, Record<string, ComponentParameter>> = {};
    for (const element of elements) {
      const inner: Record<string, ComponentParameter> = {};
      const scopeKey = mappingSelectionKey(element, mappingMode);

      if (mappingMode === "grouped") {
        for (const m of mappings) {
          if (
            m.componentName ||
            m.elementQualifiedName !== element.elementQualifiedName
          ) {
            continue;
          }
          const param = resolveParam(element.parameters, m);
          if (param) inner[m.columnName] = param;
        }
      } else {
        const componentMappings = mappings.filter(
          (m) =>
            m.componentName === element.componentCode &&
            m.elementQualifiedName === element.elementQualifiedName,
        );
        const legacyMappings = mappings.filter(
          (m) =>
            !m.componentName &&
            m.elementQualifiedName === element.elementQualifiedName,
        );
        const applicable =
          componentMappings.length > 0 ? componentMappings : legacyMappings;
        for (const m of applicable) {
          const param = resolveParam(element.parameters, m);
          if (param) inner[m.columnName] = param;
        }
      }

      if (Object.keys(inner).length > 0) {
        sel[scopeKey] = inner;
      }
    }
    return sel;
  }, [elements, mappingsQuery.data, mappingMode]);

  const {
    selections,
    setSelection,
    isReady,
    elementsMissingMappings,
    buildMappingsSavePayload,
  } = useMapColumns(elements, dataColumns, mappingMode, initialSelections);

  const saveMappings = useSaveMappings(companyCode);

  const importNotes: string[] = [];
  if (preview && preview.duplicateHeaders.length > 0) {
    importNotes.push(formatDuplicateHeadersWarning(preview.duplicateHeaders));
  }
  if (preview && !activeComponentColumn) {
    importNotes.push(
      "No component column detected. Pick the column that holds the component name.",
    );
  }
  if (hasUnresolvedAmbiguity) {
    importNotes.push(
      "Resolve ambiguous component names before validating the import.",
    );
  }

  const handleValidate = async () => {
    setIsSaving(true);
    setSaveError(null);
    setMappingValidationError(null);
    setHighlightUnmappedScopes(false);

    if (hasUnresolvedAmbiguity) {
      setMappingValidationError(
        "Choose which component instance to import for each ambiguous name before continuing.",
      );
      setIsSaving(false);
      return;
    }

    if (dataColumns.length > 0 && elementsMissingMappings.length > 0) {
      setMappingValidationError(
        formatMissingMappingsMessage(
          elementsMissingMappings,
          mappingMode,
          componentNamesByElement,
        ),
      );
      setHighlightUnmappedScopes(true);
      setIsSaving(false);
      return;
    }

    try {
      const payload = buildMappingsSavePayload();
      await saveMappings.mutateAsync(payload);
      await runImport(true);
    } catch (err) {
      setSaveError(formatUserFacingError(err));
    } finally {
      setIsSaving(false);
    }
  };

  const previewRows = useMemo(() => preview?.rows ?? [], [preview]);
  const pageSize = state.pageSize ?? DEFAULT_PAGE_SIZE;
  const { page, setPage, pageCount, pageItems, start, total } = usePagination(
    previewRows,
    pageSize,
  );
  const showPager =
    previewLayout === "flat" && total > PAGE_SIZE_OPTIONS[0];

  const previewGroups = useMemo(
    () => groupPreviewRowsByComponent(previewRows, activeComponentColumn),
    [previewRows, activeComponentColumn],
  );

  const isBusy = isParsing || isSaving || isValidating || isCommitting;

  useEffect(() => {
    reportUiBusy(isParsing || isSaving);
    return () => reportUiBusy(false);
  }, [isParsing, isSaving, reportUiBusy]);

  useEffect(() => {
    previewGroupsSeededRef.current = false;
    setOpenPreviewGroups(new Set());
  }, [activeComponentColumn, preview?.rows.length]);

  useEffect(() => {
    if (
      previewLayout !== "byComponent" ||
      previewGroupsSeededRef.current ||
      previewGroups.length === 0
    ) {
      return;
    }
    previewGroupsSeededRef.current = true;
    setOpenPreviewGroups(new Set([previewGroups[0].label]));
  }, [previewLayout, previewGroups]);

  const togglePreviewGroup = (label: string) => {
    setOpenPreviewGroups((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };

  const isMappingLoading = paramsQuery.isLoading || mappingsQuery.isLoading;
  const showMappingPanel =
    preview && !parseError && elements.length > 0 && !isMappingLoading;

  const handleRequestCancelImport = () => {
    if (isBusy) return;
    if (hasPreview) {
      setCancelConfirmOpen(true);
      return;
    }
    discardUnsavedProgress();
    void navigate(PAY_COMPONENT_IMPORT_BASE_PATH);
  };

  const handleConfirmCancelImport = () => {
    setCancelConfirmOpen(false);
    discardUnsavedProgress();
    void navigate(PAY_COMPONENT_IMPORT_BASE_PATH);
  };

  const isUnrecognizedRow = (row: Record<string, string>): boolean => {
    if (!paramsReady || !activeComponentColumn) return false;
    const value = (row[activeComponentColumn] ?? "").trim();
    if (!value) return true;
    return unknownComponentSet.has(value);
  };

  const previewIssuesForRow = (
    row: Record<string, string>,
  ): string[] => {
    if (!paramsReady || !activeComponentColumn) return [];
    const value = (row[activeComponentColumn] ?? "").trim();
    if (!value) {
      return ["Component name is blank."];
    }
    if (unknownComponentSet.has(value)) {
      return [`Unknown component name: '${value}'`];
    }
    return [];
  };

  const handlePreviewRowSave = (draft: PayComponentRowDraft) => {
    if (!preview || editingRowIndex === null) return;
    const nextPreview = applyPayComponentRowDraftToPreview(
      preview,
      editingRowIndex,
      draft,
      activeComponentColumn,
      {
        startDateColumn: state.startDateColumn,
        endDateColumn: state.endDateColumn,
      },
    );
    const csvText = serializePreviewToCsvText(nextPreview);
    updateState({
      preview: nextPreview,
      csvText,
      file: csvTextToFile(csvText, state.file?.name ?? "import.csv"),
      // Only track edited rows once a validation result exists (Review grouping).
      ...(state.importResult
        ? {
            editedRowIndexes: addEditedRowIndex(
              state.editedRowIndexes,
              editingRowIndex,
            ),
          }
        : {}),
    });
    invalidateValidation();
    setEditingRowIndex(null);
  };

  const editingSourceRow =
    editingRowIndex !== null && preview
      ? preview.rows[editingRowIndex - 1] ?? null
      : null;
  const editingMappedRow =
    editingRowIndex !== null && editingSourceRow
      ? mappedRowFromPreviewSource(
          editingRowIndex,
          editingSourceRow,
          preview!.headers,
          activeComponentColumn,
          {
            startDateColumn: state.startDateColumn,
            endDateColumn: state.endDateColumn,
          },
        )
      : null;

  const renderPreviewTable = (
    tableRows: Array<{ rowIndex: number; row: Record<string, string> }>,
    ariaLabel: string,
  ) => (
    <div
      className={styles.tableWrapper}
      role="region"
      aria-label={ariaLabel}
      tabIndex={0}
    >
      <table className={styles.previewTable}>
        <thead>
          <tr>
            <th scope="col" className={styles.actionsColumn} aria-label="Actions">
              {/* Intentionally empty — keeps the sticky Edit column narrow */}
            </th>
            {preview!.headers.map((header) => (
              <th
                key={header}
                className={
                  isReservedColumn(header)
                    ? styles.reservedColumn
                    : styles.dataColumn
                }
                scope="col"
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {tableRows.map(({ rowIndex, row }) => (
            <tr
              key={rowIndex}
              className={isUnrecognizedRow(row) ? styles.unrecognizedRow : undefined}
            >
              <td className={styles.actionsCell}>
                <Button
                  variant="tertiary"
                  onPress={() => setEditingRowIndex(rowIndex + 1)}
                  isDisabled={isBusy}
                  aria-label={`Edit row ${rowIndex + 1}`}
                >
                  Edit
                </Button>
              </td>
              {preview!.headers.map((header) => (
                <td key={header} className={styles.cell}>
                  {row[header] ?? ""}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderGroupedPreview = (groups: PreviewComponentGroup[]) => (
    <div className={styles.previewGroups}>
      {groups.map((group) => {
        const isOpen = openPreviewGroups.has(group.label);
        const unrecognized =
          paramsReady &&
          isUnrecognizedPreviewGroup(group.label, unknownComponentSet);
        return (
          <article
            key={group.label}
            className={`${styles.previewGroup} ${unrecognized ? styles.previewGroupUnrecognized : ""}`}
            data-testid={`preview-group-${group.label}`}
          >
            <button
              type="button"
              className={styles.previewGroupHeader}
              aria-expanded={isOpen}
              aria-label={`${group.label}, ${group.rows.length} ${group.rows.length === 1 ? "row" : "rows"}${unrecognized ? ", unrecognized" : ""}, ${isOpen ? "expanded" : "collapsed"}`}
              onClick={() => togglePreviewGroup(group.label)}
            >
              <span className={styles.previewGroupTitle}>
                {group.label}
                {unrecognized ? (
                  <span className={styles.unrecognizedBadge}>Unrecognized</span>
                ) : null}
              </span>
              <span
                className={`${styles.previewGroupCount} ${unrecognized ? styles.previewGroupCountWarn : ""}`}
              >
                {group.rows.length}
              </span>
            </button>
            {isOpen && (
              <div className={styles.previewGroupBody}>
                {renderPreviewTable(
                  group.rows,
                  `CSV preview for ${group.label}`,
                )}
              </div>
            )}
          </article>
        );
      })}
    </div>
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <H2>Upload and map pay components</H2>
        <Body>
          Upload a CSV, choose the component column, map parameters, then validate
          before reviewing and importing.
        </Body>
      </div>

      <section className={styles.section} aria-labelledby="upload-section-heading">
        <H3 id="upload-section-heading">Select file</H3>
        <FileDropzone
          value={state.file}
          compactWhenSelected
          onChange={handleFileChange}
          instruction="Drop your CSV file here, or click to browse"
          subInstruction="Accepted format: CSV (.csv). Include Employee Code and a component column (or pick it below)."
          accept=".csv"
          multiple={false}
          isDisabled={isBusy || pendingReplacementFile !== null}
          buttonText="Select CSV file"
        />
        {state.file && !parseError && (
          <p className={styles.selectedFile} role="status">
            <strong>{preview?.rows.length ?? 0} row(s) parsed</strong>
            {state.file ? ` from ${state.file.name}` : ""}.
          </p>
        )}
        {isParsing && (
          <p className={styles.selectedFile} role="status">Parsing file…</p>
        )}
      </section>

      {parseError && (
        <section className={styles.section} aria-labelledby="parse-error-heading">
          <div className={styles.warning} role="alert">
            <h2 id="parse-error-heading" className={styles.parseSummaryHeading}>
              File not imported
            </h2>
            <p className={styles.parseGuidance}>{parseError}</p>
            <p className={styles.parseGuidance}>
              Fix the file and upload again. No preview is shown for this attempt.
            </p>
          </div>
        </section>
      )}

      {importNotes.length > 0 && preview && !parseError && (
        <section className={styles.section} aria-labelledby="import-notes-heading">
          <div className={styles.notice} role="status">
            <h2 id="import-notes-heading" className={styles.parseSummaryHeading}>
              {importNotes.length} import note{importNotes.length === 1 ? "" : "s"}
            </h2>
            <details className={styles.issueDetails}>
              <summary className={styles.issueDetailsSummary}>
                View import notes
              </summary>
              <ul className={styles.noteList}>
                {importNotes.map((note) => (
                  <li key={note}>{note}</li>
                ))}
              </ul>
            </details>
          </div>
        </section>
      )}

      {preview && !parseError && (
        <>
          <div className={styles.splitRow}>
            <section className={styles.splitCol} aria-labelledby="components-heading">
              <H3 id="components-heading">
                Components found ({componentNames.length})
              </H3>
              {componentNames.length > 0 ? (
                <ul className={styles.deductionList} aria-label="Distinct component names">
                  {componentNames.map((name) => {
                    const unrecognized =
                      paramsReady && unknownComponentSet.has(name);
                    return (
                      <li
                        key={name}
                        className={`${styles.deductionItem} ${unrecognized ? styles.deductionItemUnrecognized : ""}`}
                        title={
                          unrecognized
                            ? "Does not match a component for this company"
                            : undefined
                        }
                      >
                        {name}
                        {unrecognized ? " · unrecognized" : ""}
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className={styles.notice}>
                  No component column detected. Pick the component column on the right.
                </p>
              )}
            </section>

            {preview.headers.length > 0 && (
              <section
                className={styles.splitCol}
                aria-labelledby="component-column-heading"
              >
                <H3 id="component-column-heading">Component column</H3>
                <select
                  id="component-column"
                  className={styles.textInput}
                  value={activeComponentColumn}
                  disabled={isBusy}
                  onChange={(e) => {
                    invalidateValidation();
                    updateState({ componentColumn: e.target.value || null });
                  }}
                  aria-labelledby="component-column-heading"
                >
                  <option value="">— auto-detect (Component Name / Deduction Name) —</option>
                  {preview.headers.map((header) => (
                    <option key={header} value={header}>{header}</option>
                  ))}
                </select>
                <p className={styles.fieldHint}>
                  Which CSV column names the component for each row.
                </p>
              </section>
            )}
          </div>

          {showUnrecognizedWarning && (
            <div className={styles.unrecognizedBanner} role="alert">
              <strong>
                {unrecognizedSummary.unknownNames.length > 0
                  ? `${unrecognizedSummary.unknownNames.length} unrecognized component${unrecognizedSummary.unknownNames.length === 1 ? "" : "s"}`
                  : "Unrecognized rows"}
              </strong>
              {unrecognizedSummary.unknownNames.length > 0
                ? `: ${unrecognizedSummary.unknownNames.join(", ")}. `
                : ". "}
              {unrecognizedSummary.unknownRowCount > 0 && (
                <>
                  {unrecognizedSummary.unknownRowCount}{" "}
                  {unrecognizedSummary.unknownRowCount === 1 ? "row uses" : "rows use"}{" "}
                  a name that does not match this company
                  {unrecognizedSummary.blankRowCount > 0 ? "; " : ". "}
                </>
              )}
              {unrecognizedSummary.blankRowCount > 0 && (
                <>
                  {unrecognizedSummary.blankRowCount}{" "}
                  {unrecognizedSummary.blankRowCount === 1
                    ? "row has"
                    : "rows have"}{" "}
                  a blank component name.{" "}
                </>
              )}
              These rows cannot be mapped and will be rejected on validate. Fix
              them in the CSV and re-upload, or continue mapping the recognised
              components.
            </div>
          )}

          {preview.rows.length > 0 && (
            <section className={styles.section} aria-labelledby="preview-heading">
              <div className={styles.previewHeader}>
                <H3 id="preview-heading">Preview ({total} rows)</H3>
                <div
                  className={styles.previewModeToggle}
                  role="group"
                  aria-label="Preview layout"
                >
                  <button
                    type="button"
                    className={`${styles.previewModeButton} ${previewLayout === "byComponent" ? styles.previewModeActive : ""}`}
                    aria-pressed={previewLayout === "byComponent"}
                    onClick={() => setPreviewLayout("byComponent")}
                  >
                    By component
                  </button>
                  <button
                    type="button"
                    className={`${styles.previewModeButton} ${previewLayout === "flat" ? styles.previewModeActive : ""}`}
                    aria-pressed={previewLayout === "flat"}
                    onClick={() => setPreviewLayout("flat")}
                  >
                    All rows
                  </button>
                </div>
              </div>

              {previewLayout === "flat"
                ? renderPreviewTable(
                    pageItems.map((row, rowIndex) => ({
                      rowIndex: start + rowIndex,
                      row,
                    })),
                    "CSV preview table",
                  )
                : renderGroupedPreview(previewGroups)}

              {showPager && (
                <Pagination
                  page={page}
                  pageCount={pageCount}
                  total={total}
                  pageSize={pageSize}
                  onChange={setPage}
                  onPageSizeChange={(n) =>
                    updateState({ pageSize: clampPageSize(n) })
                  }
                />
              )}
            </section>
          )}
        </>
      )}

      {preview && !parseError && isMappingLoading && (
        <Body>Loading components and saved mappings…</Body>
      )}

      {showMappingPanel && (
        <section className={styles.mappingSection} aria-labelledby="mapping-heading">
          <H3 id="mapping-heading">Map columns</H3>
          {mappingsQuery.error && (
            <div className={styles.notice} role="alert">
              Could not load saved mappings: {formatUserFacingError(mappingsQuery.error)}.
              You can still map columns manually.
            </div>
          )}
          <AssignmentDateColumnsFields
            headers={headers}
            startDateColumn={activeStartDateColumn}
            endDateColumn={activeEndDateColumn}
            componentColumn={activeComponentColumn}
            disabled={isBusy}
            onStartDateColumnChange={(column) => {
              updateState({ startDateColumn: column });
              invalidateValidation();
            }}
            onEndDateColumnChange={(column) => {
              updateState({ endDateColumn: column });
              invalidateValidation();
            }}
          />
          <MappingTablesPanel
            mappingMode={mappingMode}
            onMappingModeChange={(mode) => {
              invalidateValidation();
              setMappingMode(mode);
            }}
            hasSharedElements={hasSharedElements}
            controlsDisabled={isBusy}
            unknownComponents={unknownComponents}
            showUnknownComponentsBanner={false}
            ambiguousComponents={ambiguousComponents}
            resolutions={resolutions}
            onSelectInstance={handleSelectInstance}
            elements={elements}
            componentNamesByElement={componentNamesByElement}
            dataColumns={dataColumns}
            selections={selections}
            setSelection={setSelection}
            highlightUnmappedScopes={highlightUnmappedScopes}
            elementsMissingMappings={elementsMissingMappings}
            onMappingFieldChange={() => {
              invalidateValidation();
              setMappingValidationError(null);
              setHighlightUnmappedScopes(false);
            }}
          />
        </section>
      )}

      {mappingValidationError && (
        <div className={styles.errorBanner} role="alert">
          <p>
            <strong>Mapping required:</strong> {mappingValidationError}
          </p>
        </div>
      )}

      {saveError && (
        <div className={styles.errorBanner} role="alert">
          <p>
            <strong>Validate failed:</strong> {saveError}
          </p>
        </div>
      )}

      <WorkflowActionBar
        ariaLabel="Pay component import actions"
        status={
          state.validationStale && state.importResult
            ? "Mappings or file changed since the last validation. Validate again before reviewing."
            : undefined
        }
        leadingActions={
          hasPreview ? (
            <Button
              variant="secondary"
              onPress={handleRequestCancelImport}
              isDisabled={isBusy}
            >
              Cancel import
            </Button>
          ) : undefined
        }
        trailingActions={
          <Button
            variant="primary"
            isDisabled={
              !preview ||
              preview.rows.length === 0 ||
              !isReady ||
              isBusy ||
              state.mypayEnvironment === null ||
              elements.length === 0
            }
            onPress={() => void handleValidate()}
          >
            {isSaving || isValidating ? "Validating…" : "Validate"}
          </Button>
        }
      />

      <ReplaceCsvConfirmModal
        isOpen={pendingReplacementFile !== null}
        fileName={pendingReplacementFile?.name ?? ""}
        rowCount={preview?.rows.length ?? 0}
        onKeepCurrent={() => setPendingReplacementFile(null)}
        onReplace={handleConfirmReplacement}
      />

      <CancelImportConfirmModal
        isOpen={cancelConfirmOpen}
        onKeepImporting={() => setCancelConfirmOpen(false)}
        onCancelAnyway={handleConfirmCancelImport}
      />

      <UnsavedProgressConfirmModal
        isOpen={isBlocked}
        title="Leave pay component import?"
        message="Uploaded rows, mappings, and validation progress will be lost."
        onStay={stay}
        onLeave={handleLeaveAnyway}
      />

      <PayComponentRowEditModal
        isOpen={editingRowIndex !== null}
        row={editingMappedRow}
        sourceRow={editingSourceRow}
        headers={preview?.headers ?? []}
        componentColumn={activeComponentColumn}
        startDateColumn={state.startDateColumn}
        endDateColumn={state.endDateColumn}
        rowErrors={
          editingSourceRow ? previewIssuesForRow(editingSourceRow) : []
        }
        isProcessing={isBusy}
        onCancel={() => setEditingRowIndex(null)}
        onSave={handlePreviewRowSave}
      />
    </div>
  );
}
